import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Button, Dimmer, Loader, Message } from 'semantic-ui-react';
import { defineMessages, injectIntl } from 'react-intl';
import { Icon } from '@plone/volto/components';
import withObjectBrowser from '@plone/volto/components/manage/Sidebar/ObjectBrowser';
import { createContent } from '@plone/volto/actions';
import { flattenToAppURL, getBaseUrl } from '@plone/volto/helpers';

import audioBlockSVG from './block-audio.svg';
import clearSVG from '@plone/volto/icons/clear.svg';
import navTreeSVG from '@plone/volto/icons/nav.svg';
import uploadSVG from '@plone/volto/icons/upload.svg';

import './audioBlock.css';

const messages = defineMessages({
  addAudio: {
    id: 'Browse the site or upload an audio file',
    defaultMessage: 'Browse the site or upload an audio file',
  },
  pickAnAudio: {
    id: 'Browse for an audio file',
    defaultMessage: 'Browse for an audio file',
  },
  uploadAnAudio: {
    id: 'Upload an audio file from your computer',
    defaultMessage: 'Upload an audio file from your computer',
  },
  uploadingAudio: {
    id: 'Uploading audio',
    defaultMessage: 'Uploading audio',
  },
});

class Edit extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      uploading: false,
    };
    this.fileInputRef = React.createRef();
  }

  onUploadAudio = (e) => {
    e.stopPropagation();
    const file = e.target.files[0];
    if (!file) return;

    this.setState({ uploading: true });

    const reader = new FileReader();
    reader.onload = (event) => {
      const dataURL = event.target.result;
      const fields = dataURL.match(/^data:(.*);(.*),(.*)$/);

      this.props
        .createContent(
          getBaseUrl(this.props.pathname),
          {
            '@type': 'File',
            title: file.name,
            file: {
              data: fields[3],
              encoding: fields[2],
              'content-type': fields[1],
              filename: file.name,
            },
          },
          this.props.block,
        )
        .then((response) => {
          const baseUrl = flattenToAppURL(response['@id'] || '');
          const downloadUrl = `${baseUrl}/@@download/file/${file.name}`;
          this.setState({ uploading: false });
          this.props.onChangeBlock(this.props.block, {
            ...this.props.data,
            url: downloadUrl,
          });
        })
        .catch((error) => {
          console.error('Error uploading audio:', error);
          this.setState({ uploading: false });
        });
    };

    reader.readAsDataURL(file);
  };

  onClearAudio = () => {
    this.props.onChangeBlock(this.props.block, {
      ...this.props.data,
      url: '',
    });
  };

  render() {
    const { block, data, intl, openObjectBrowser } = this.props;
    const { uploading } = this.state;

    return (
      <div className="block audioblock edit">
        {data.url ? (
          // ── Preview state ──────────────────────────────────────────────────
          <div className="audio-upload-widget-image">
            <div className="audio-upload-widget-toolbar">
              <Button.Group>
                <Button icon basic onClick={this.onClearAudio}>
                  <Icon
                    className="circled"
                    name={clearSVG}
                    size="24px"
                    color="#e40166"
                  />
                </Button>
              </Button.Group>
            </div>
            <audio controls src={data.url}>
              Your browser does not support the audio element.
            </audio>
          </div>
        ) : (
          // ── Empty state ────────────────────────────────────────────────────
          <div className="audio-upload-widget">
            <Message>
              {uploading && (
                <Dimmer active>
                  <Loader indeterminate>
                    {intl.formatMessage(messages.uploadingAudio)}
                  </Loader>
                </Dimmer>
              )}
              <img src={audioBlockSVG} alt="" className="placeholder" />
              <p>{intl.formatMessage(messages.addAudio)}</p>
              <div className="toolbar-wrapper">
                <div className="toolbar-inner">
                  {/* Browse site */}
                  <Button.Group>
                    <Button
                      aria-label={intl.formatMessage(messages.pickAnAudio)}
                      icon
                      basic
                      onClick={(e) => {
                        e.preventDefault();
                        openObjectBrowser({
                          mode: 'link',
                          selectableTypes: ['File'],
                          onSelectItem: (url) => {
                            const downloadUrl = `${flattenToAppURL(url)}/@@download/file`;
                            this.props.onChangeBlock(block, {
                              ...data,
                              url: downloadUrl,
                            });
                          },
                        });
                      }}
                      type="button"
                    >
                      <Icon name={navTreeSVG} size="24px" />
                    </Button>
                  </Button.Group>

                  {/* Upload from computer */}
                  <Button.Group>
                    <Button
                      aria-label={intl.formatMessage(messages.uploadAnAudio)}
                      icon
                      basic
                      compact
                      onClick={() => {
                        this.fileInputRef.current.click();
                      }}
                      type="button"
                    >
                      <Icon name={uploadSVG} size="24px" />
                    </Button>
                    <input
                      ref={this.fileInputRef}
                      type="file"
                      onChange={this.onUploadAudio}
                      style={{ display: 'none' }}
                      accept="audio/*"
                    />
                  </Button.Group>
                </div>
              </div>
            </Message>
          </div>
        )}
      </div>
    );
  }
}

export default compose(
  injectIntl,
  withObjectBrowser,
  connect(
    (state) => ({
      pathname: state.router?.location?.pathname,
    }),
    { createContent },
  ),
)(Edit);
